import { useCurrentCompany, useCurrentUser } from "hooks/auth";
import { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { logoutUser as logoutUserSlice, setIsAuthLoading } from "../authSlice";
import { fetchCurrentUserOnce } from "../utils";
import { logErrorIfDebug } from "utils/logging";
import { showToast } from "utils/helpers";
import { apiV1 } from "api-v2";
import { removeAllCookie } from "utils/cookiesHelper";
import { toast } from "react-toastify";
import { SessionEvent, useSessionBroadcast } from "./useSessionChannel";
import { useAppSelector } from "newui/store/hooks";
import { selectAuthLoading } from "../authSelectors";

export interface AuthManager {
	errorMessage: string | null;
	isSuspended: boolean;
	isVerified: boolean;
	logoutUser: () => void;
}

/**
 * The Auth Manager processes the current User logged-in state
 * When user first hits the site, we want to ensure that they have a valid access token
 * This is stored in secure cookies - so we have to hit the user profile endpoint to see if we get a 403 or not
 * @returns
 */
export const useAuthManager = (): AuthManager => {
	const [errorMessage, setErrorMessage] = useState<string | null>(null);
	const isAuthLoading = useAppSelector(selectAuthLoading);
	const dispatch = useDispatch();

	const { currentUser } = useCurrentUser();
	const currentCompany = useCurrentCompany();

	const processLogout = useCallback(() => {
		try {
			// ensure we are going to home page - this prevents any redirect saves
			window.history.replaceState({}, "", "/");

			// logout frontend
			dispatch(logoutUserSlice());

			// clear cookies and storage
			localStorage.clear();
			removeAllCookie();
		} catch (error) {
			toast("Logout failed:", error);
		}
	}, [dispatch]);

	const { sendSessionBroadcast } = useSessionBroadcast(async (msg) => {
		if (msg.type === SessionEvent.START_LOGOUT) {
			dispatch(setIsAuthLoading(true));
		}

		if (msg.type !== SessionEvent.LOGOUT_COMPLETE) {
			return;
		}

		processLogout();
		dispatch(setIsAuthLoading(false));
	});

	const logoutUser = useCallback(async () => {
		dispatch(setIsAuthLoading(true));
		processLogout();
		await new Promise((resolve) => setTimeout(resolve, 0));
		try {
			sendSessionBroadcast(SessionEvent.START_LOGOUT);
			await apiV1.post("auth/logout");
		} finally {
			dispatch(setIsAuthLoading(false));
			sendSessionBroadcast(SessionEvent.LOGOUT_COMPLETE);
		}
	}, [dispatch, processLogout, sendSessionBroadcast]);

	// currentUser not set yet and not currently fetching
	useEffect(() => {
		// wait for logout to finish before processing anything else
		if (isAuthLoading || currentUser) {
			setErrorMessage(null);
			return;
		}

		fetchCurrentUserOnce(dispatch).catch((err) => {
			// we expect a 401 here
			const status = err.request?.status;

			if (status !== 401) {
				// show an error
				logErrorIfDebug("Pre-auth fetch failed (NOT expected 401):", err);
				const message = `Failed to verify user, please try again. Status Code: ${status}`;
				setErrorMessage(message);
				showToast(message, "User verification failed", false, "UserVerify");
			}
		});
	}, [currentUser, isAuthLoading, dispatch]);

	return {
		isVerified: currentUser?.email_verified ? true : false,
		isSuspended: !currentCompany?.active,
		errorMessage: errorMessage,
		logoutUser: logoutUser,
	};
};
